import Accordion from "components/blocks/Accordion";
import AccordionItem from "components/blocks/Accordion/AccordionItem";
import FileUploadWithPreview from "components/blocks/FileUploadWithPreview";
import FormInput from "components/blocks/FormInput";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import { useTranslation } from "react-i18next";
import LookupDropdown from "components/blocks/LookupDropdown";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import FormViewComponent from "components/formBuilder/FormViewComponent";
import { ScientificClassificationResult } from "services/scientificPublicationsService/models/scientificPublicationsTypes";
import useOrderForm from "../../../../../OrderForm/useOrderForm";
import FormTextArea from "../../../../../../../../components/blocks/FormTextArea";
import RulesAndConditionSection from "../../../../../OrderForm/components/RulesAndConditionSection";
import PersonalInfo from "../../../../../OrderForm/components/PersonalInfo";
import {useEffect} from "react";
import {
    CourseOrderLocalItemResult,
    CourseOrderStatus
} from "../../../../../../../../services/courseOrder/models/CourseOrderType";
import {TaskActionsNames} from "../../../../../../../../services/shared-models/sharedTypes";
import {OpenPublishingTaskResult} from "../../../../../../../../services/tasks/models/tasksTypes";
import {BackendServices} from "../../../../../../../../services";
import {CourseOrderService} from "../../../../../../../../services/courseOrder";
import {useParams} from "react-router-dom";


type UpdateAuthorFromProps ={
    order:CourseOrderLocalItemResult|null,
    submitRef?: any;
    isFile?:boolean;
    isReadOny?:boolean;
}
const UpdateAuthorFrom = ({order,submitRef,isFile,isReadOny}:UpdateAuthorFromProps) => {
    // Hooks
    const { toSystemThemeDateFormat } = useSystemDateTheme();
    const { serialGID } = useParams();
    const { t, i18n } = useTranslation("Orders");
    const  isPublishingHouse =  !!order?.publishingOrderType?.id;
    const {
        errors,
        onSubmit,
        getValues,
        reset,
        register,
        watch,
        setValue,
        trigger,
        courseData,
        isLoading,
        researcherAttachmentWithoutInfo,
        researcherAttachments,
        formSchema,
        setFormSchema,
        setResearcherAttachmentWithoutInfo,
        setResearcherAttachments,
    } = useOrderForm({isPublishingHouse});
    

    const isArabic = i18n.language === "ar";

    useEffect(() => {
        if(order)
        {
            setResearcherAttachments(
                order.researcherAttachment.map((file) => ({
                    file: new File([], file.name ?? ""),
                    fileName: file.name ?? "",
                    uuid: file.id.toString(),
                    size: file.size,
                    showDeleteButton: !isFile && !isReadOny,
                    createdBy: file.createdBy,
                    createdOn: file.createdOn,

                })),
            );

            setResearcherAttachmentWithoutInfo(
                order.researcherAttachmentWithoutInfo.map((file) => ({
                    file: new File([], file.name ?? ""),
                    fileName: file.name ?? "",
                    uuid: file.id.toString(),
                    size: file.size,
                    showDeleteButton: !isFile && !isReadOny,
                    createdBy: file.createdBy,
                    createdOn: file.createdOn,
                })),
            );

            setFormSchema(order.scientificClassification &&order.scientificClassification.jsonSchema ? JSON.parse(order.scientificClassification.jsonSchema) : {});
            setTimeout(()=>{
                setValue("formValue", order.formValue);
            },1000)


            reset({
                ...order,
                researcherAttachmentIds: order.researcherAttachment.map((x:any) => x.id),
                researcherAttachmentWithoutInfoIds: order.researcherAttachmentWithoutInfo.map((x:any) => x.id),
                scientificClassificationId: order.scientificClassification.id,
                scientificSpecializationId: order.scientificSpecialization.id,
                typeOfScientificCategoryId: order.typeOfScientificCategory.id,
                typeOfScientificId: order.typeOfScientific.id,
                languageOfScientificId: order.languageOfScientific.id,
                centreManagementId: order.centreManagementId==0?  null : order.centreManagementId,
                publishingOrderTypeId : order.publishingOrderType?.id,
                isDraft: order.courseOrderStatusId === CourseOrderStatus.Draft.id,
                courseId: order.courseId,
                formValue: order.formValue,
            });

        }

    }, [order]);

    // Sync researcherAttachments with form state
    useEffect(() => {
        setValue(
            "researcherAttachmentIds",
            researcherAttachments.map((x) => x.uuid),
        );
    }, [researcherAttachments, setValue]);

    // Sync researcherAttachmentWithoutInfo with form state
    useEffect(() => {
        setValue(
            "researcherAttachmentWithoutInfoIds",
            researcherAttachmentWithoutInfo.map((x) => x.uuid),
        );
    }, [researcherAttachmentWithoutInfo, setValue]);

    
    useEffect(()=>{
        submitRef.current = {
            globalSubmit:async (actionName: TaskActionsNames,openTask: OpenPublishingTaskResult | null,notes?:string,filesIds?:string[] ) :Promise<number>=>{
                if(actionName ==="Complete")
                {
                    // For read-only mode, skip validation as user cannot edit fields
                    if (!isReadOny) {
                        // Trigger form validation for editable fields
                        const isValid = await trigger();

                        // If validation fails, return -1 to prevent submission
                        if (!isValid) {
                            return -1;
                        }
                    }

                    const updatedOrder= getValues();
                    updatedOrder.serialGuid = serialGID;
                    updatedOrder.actionName = actionName;
                    updatedOrder.notes = notes;
                    updatedOrder.activityGuid = openTask?.activityGuid??"";
                    updatedOrder.actionsFileIds = filesIds??[];
                    await CourseOrderService.updateCourseOrder(order?.id??0,{...updatedOrder})
                    return 1;
                }
                return 0;



            }
        };

    })
    // @ts-ignore
    return (
        <form className="position-relative" noValidate >
            <LoadingOverlay visible={isLoading} className="position-fixed" />
              <Accordion negatePadding>
                <AccordionItem initiallyOpen title={t("applicantInfo")} elementId={1}>
                    <PersonalInfo  />
                </AccordionItem>
            </Accordion>

            <div className="divider" />

            <Accordion negatePadding>
                <AccordionItem initiallyOpen title={t("applicationForm")} elementId={2}>
                    <div className="d-flex flex-column gap-2">
                        <div className="row">
                            {isPublishingHouse &&
                                <LookupDropdown
                                    wrapperClassName="col-lg-3 col-md-6 col-sm-12 mb-2"
                                    label={t("PublishingOrderTypes")}
                                    service="lookupService"
                                    endpoint="getAllPublishingOrderTypesDropDown"
                                    noSelectionPlaceholder={t("PublishingOrderTypes")}
                                    onChange={(value) => {
                                        setValue("publishingOrderTypeId", +value);
                                    }}
                                    disabled={isReadOny}
                                    value={watch("publishingOrderTypeId")}
                                    textValueKey={`details.${isArabic ? 0 : 1}.displayName`}
                                    idValueKey="id"
                                    error={t(errors.publishingOrderTypeId?.message ?? "")}
                                    useReactSelect
                                />

                            }
                            <LookupDropdown
                                wrapperClassName="col-lg-3 col-md-6 col-sm-12 mb-2"
                                label={t("scientificSpecializationOfThePublishedMaterial")}
                                service="lookupService"
                                disabled={isReadOny}
                                endpoint="getAllScientificSpecializationDropDown"
                                noSelectionPlaceholder={t("scientificSpecializationOfThePublishedMaterial")}
                                onChange={(value) => {
                                    setValue("scientificSpecializationId", +value);
                                }}
                                value={watch("scientificSpecializationId")}
                                textValueKey={`details.${isArabic ? 0 : 1}.displayName`}
                                idValueKey="id"
                                error={t(errors.scientificSpecializationId?.message ?? "")}
                                useReactSelect
                            />
                            <LookupDropdown
                                wrapperClassName="col-lg-3 col-md-6 col-sm-12 mb-2"
                                label={t("languageOfScientificPublishing")}
                                service="lookupService"
                                disabled={isReadOny}
                                endpoint="getAllLanguageOfScientificDropDown"
                                onChange={(value) => {
                                    setValue("languageOfScientificId", +value);
                                }}
                                value={watch("languageOfScientificId")}
                                textValueKey={`details.${isArabic ? 0 : 1}.displayName`}
                                idValueKey="id"
                                error={t(errors.languageOfScientificId?.message ?? "")}
                                useReactSelect
                            />
                  
                            <LookupDropdown
                                wrapperClassName="col-lg-3 col-md-6 col-sm-12 mb-2"
                                label={t("typeOfScientificPublishing")}
                                service="lookupService"
                                disabled={isReadOny}
                                endpoint="getAllTypeOfScientificByTypeDropDown"
                                query={{"id":+watch("typeOfScientificCategoryId")}}
                                onChange={(value) => {
                                    setValue("typeOfScientificId", +value);
                                    setValue("scientificClassificationId", 0);
                                }}
                                value={+watch("typeOfScientificId")}
                                textValueKey={`details.${isArabic ? 0 : 1}.displayName`}
                                idValueKey="id"
                                error={t(errors.typeOfScientificId?.message ?? "")}
                                useReactSelect
                            />
                            <LookupDropdown
                                wrapperClassName="col-lg-3 col-md-6 col-sm-12 mb-2"
                                label={t("scientificClassification")}
                                service="lookupService"
                                disabled={isReadOny}
                                endpoint="getAllScientificClassificationByTypeDropDown"
                                query={{"id":+watch("typeOfScientificId")} }
                                onChange={(value, item, dbRes) => {
                                    dbRes = dbRes as ScientificClassificationResult[];
                                    const selectedItem = dbRes?.find((x) => x.id === +value)?.jsonSchema;

                                    setFormSchema(selectedItem ? JSON.parse(selectedItem) : {});
                                    setValue("scientificClassificationId", +value);
                                    //setValue("formValue", "{}");
                                }}
                                value={+watch("scientificClassificationId")}
                                textValueKey={`scientificClassificationDetail.displayName`}
                                idValueKey="id"
                                error={t(errors.scientificClassificationId?.message ?? "")}
                                useReactSelect
                            />
                        </div>

                        {/* TODO: not ready  */}
                        {formSchema && (
                            <div className="d-flex flex-column">
                                <FormViewComponent
                                    data={watch("formValue") ? JSON.parse(watch("formValue")!) : {}}
                                    formDefinition={formSchema}
                                    readOnly={isReadOny}
                                    onChange={(values) => {
                                        setValue("isFormValueReady", values.isValid);
                                        setValue("formValue", JSON.stringify(values.data));

                                    }}
                                />
                                {errors.isFormValueReady?.message && (
                                    <span className="invalid-feedback my-2 d-block">{errors.isFormValueReady?.message}</span>
                                )}
                            </div>
                        )}

                        <div className="row">
                            <FormInput
                                type="text"
                                label={t("numberOfPages")}
                                wrapperClassName="col-lg-3 col-md-6 col-sm-12 mb-2"
                                inputClassName="form-control"
                                disabled={isReadOny}
                                placeholder={t("numberOfPages")}
                                leftElement={<span className="text-primary">{t("page")}</span>}
                                error={t(errors.numberOfPages?.message ?? "")}
                                {...register("numberOfPages", {
                                    valueAsNumber: true,
                                })}
                            />
                            {!isPublishingHouse &&  <LookupDropdown
                                wrapperClassName="col-lg-9 col-md-6 col-sm-12 mb-2"
                                label={t("researchesCenter")}
                                service="courseOrderService"
                                disabled={isReadOny}
                                endpoint="getManagementCentersDropdown"
                                onChange={(value) => {
                                    setValue("centreManagementId", +value);
                                }}
                                value={watch("centreManagementId")}
                                textValueKey="centreManagementDetail.displayName"
                                idValueKey="id"
                                error={t(errors.centreManagementId?.message ?? "")}
                                useReactSelect
                                isPaginated
                            />}
                        </div>

                        <FileUploadWithPreview
                            label={t("uploadCompleteFile")}
                            files={researcherAttachments}
                            setFiles={setResearcherAttachments}
                            isMultiple={true}
                            // AddOnly={isFile}
                            uploadWithUser={true}
                        />
                        <div className="divider" />
                        <FileUploadWithPreview
                            label={t("uploadFileWithoutAuthorInformation")}
                            files={researcherAttachmentWithoutInfo}
                            setFiles={setResearcherAttachmentWithoutInfo}
                            isMultiple={true}
                            // AddOnly={isFile}
                            uploadWithUser={true}
                        />
                    </div>
                </AccordionItem>
            </Accordion>

            <div className="divider" />
            <FormTextArea
                label={t("notes")}
                wrapperClassName="col-12 mb-2"
                disabled={isReadOny}
                inputClassName="form-control"
                placeholder={t("notes")}
                error={t(errors.researcherNotes?.message ?? "")}
                {...register("researcherNotes")}
            />
            <div className="divider" />

            <RulesAndConditionSection
                readOnly={isReadOny}
                errors={errors}
                register={register}
                courseDetails={courseData?.courseDetails.find((x) => x.locale.toLowerCase() === i18n.language.toLowerCase())}
            />
        </form>
    );
};

export default UpdateAuthorFrom;
